import { useState, useEffect } from 'react'
import { ArrowLeft, Loader2, Check, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { saveCustomNetwork, getAllNetworks } from '@/config/networks'
import networkValidationService from '@/services/blockchain/network-validation.service'
import Snackbar from '@/components/Snackbar'

type RpcStatus = 'idle' | 'validating' | 'valid' | 'invalid' | 'exists'

const AddNetwork = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [networkName, setNetworkName] = useState('')
  const [rpcName, setRpcName] = useState('')
  const [rpcUrl, setRpcUrl] = useState('')
  const [detectedChainId, setDetectedChainId] = useState<number | null>(null)
  const [currencySymbol, setCurrencySymbol] = useState('')
  const [blockExplorerUrl, setBlockExplorerUrl] = useState('')

  const [rpcStatus, setRpcStatus] = useState<RpcStatus>('idle')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbarState, setSnackbarState] = useState<{
    isOpen: boolean
    message: string
    type?: 'success' | 'error' | 'info'
  }>({ isOpen: false, message: '' })

  useEffect(() => {
    setRpcStatus('idle')
    setDetectedChainId(null)

    if (!rpcUrl.trim() || !/^https?:\/\//i.test(rpcUrl.trim())) return

    let cancelled = false
    const timer = setTimeout(async () => {
      setRpcStatus('validating')
      const result = await networkValidationService.validateRpc(rpcUrl.trim())
      if (cancelled) return

      if (!result.isValid || !result.detectedChainId) {
        setRpcStatus('invalid')
        return
      }

      const exists = getAllNetworks().some((n) => n.chainId === result.detectedChainId)
      if (exists) {
        setRpcStatus('exists')
        return
      }

      setDetectedChainId(result.detectedChainId)
      setRpcStatus('valid')
    }, 600)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [rpcUrl])

  const handlePasteTrim =
    (setter: (v: string) => void) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      setter(e.clipboardData.getData('text').trim())
    }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (rpcStatus !== 'valid' || !detectedChainId || isSubmitting) return

    setIsSubmitting(true)

    const result = saveCustomNetwork({
      id: `custom-${detectedChainId}`,
      name: networkName.trim(),
      chainId: detectedChainId,
      rpcUrl: rpcUrl.trim(),
      rpcName: rpcName.trim() || undefined,
      blockExplorerUrl: blockExplorerUrl.trim() || undefined,
      isTestnet: false,
      isCustom: true,
      nativeCurrency: {
        name: currencySymbol.trim(),
        symbol: currencySymbol.trim(),
        decimals: 18,
      },
    })

    setIsSubmitting(false)

    if (result.success) {
      setSnackbarState({ isOpen: true, message: t('add_network.success'), type: 'success' })
      setTimeout(() => navigate('/networks'), 1200)
    } else {
      setSnackbarState({
        isOpen: true,
        message: t('add_network.network_already_exists'),
        type: 'error'
      })
    }
  }

  return (
    <div className="flex flex-col w-full max-w-lg mx-auto gap-8 pt-4">
      <div className="hidden lg:flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full bg-text-main/5 hover:bg-text-main/10 transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-text-main" />
        </button>
        <h1 className="text-3xl font-bold text-text-main">
          {t('add_network.title')}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-card border border-border-base rounded-3xl p-8 flex flex-col gap-6 shadow-xl"
      >
        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.network_name')}
          </label>
          <input
            type="text"
            value={networkName}
            onChange={(e) => setNetworkName(e.target.value)}
            onPaste={handlePasteTrim(setNetworkName)}
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.rpc_name')}
          </label>
          <input
            type="text"
            value={rpcName}
            onChange={(e) => setRpcName(e.target.value)}
            onPaste={handlePasteTrim(setRpcName)}
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.rpc_url')}
          </label>
          <input
            type="text"
            value={rpcUrl}
            onChange={(e) => setRpcUrl(e.target.value)}
            onPaste={handlePasteTrim(setRpcUrl)}
            placeholder="https://..."
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main font-mono"
            required
          />
          {rpcStatus === 'validating' && (
            <p className="flex items-center gap-1.5 text-xs text-text-secondary">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t('add_network.validating')}
            </p>
          )}
          {rpcStatus === 'valid' && (
            <p className="flex items-center gap-1.5 text-xs text-success">
              <Check className="w-3.5 h-3.5" />
              {t('add_network.rpc_valid')}
            </p>
          )}
          {rpcStatus === 'invalid' && (
            <p className="flex items-center gap-1.5 text-xs text-red-500">
              <AlertCircle className="w-3.5 h-3.5" />
              {t('add_network.rpc_invalid')}
            </p>
          )}
          {rpcStatus === 'exists' && (
            <p className="flex items-center gap-1.5 text-xs text-red-500">
              <AlertCircle className="w-3.5 h-3.5" />
              {t('add_network.network_already_exists')}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.chain_id')}
          </label>
          <input
            type="text"
            value={detectedChainId ?? ''}
            placeholder={t('add_network.chain_id_placeholder')}
            disabled
            readOnly
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent outline-none transition-all text-text-main disabled:opacity-60"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.currency_symbol')}
          </label>
          <input
            type="text"
            value={currencySymbol}
            onChange={(e) => setCurrencySymbol(e.target.value)}
            onPaste={handlePasteTrim(setCurrencySymbol)}
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs text-text-secondary uppercase tracking-widest font-bold ml-1">
            {t('add_network.block_explorer_url')}{' '}
            <span className="normal-case font-normal text-text-secondary">
              {t('add_network.optional_label')}
            </span>
          </label>
          <input
            type="text"
            value={blockExplorerUrl}
            onChange={(e) => setBlockExplorerUrl(e.target.value)}
            onPaste={handlePasteTrim(setBlockExplorerUrl)}
            placeholder="https://..."
            className="w-full p-4 rounded-2xl bg-text-main/5 border border-transparent focus:border-primary/50 outline-none transition-all text-text-main font-mono"
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={rpcStatus !== 'valid' || isSubmitting}
            className="btn-gradient-fill w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{t('add_network.submit')}</span>
          </button>
        </div>
      </form>

      <Snackbar
        message={snackbarState.message}
        isOpen={snackbarState.isOpen}
        type={snackbarState.type}
        onClose={() => setSnackbarState((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  )
}

export default AddNetwork
